import { Link, useRouterState } from '@tanstack/react-router';
import { PanelLeft, Plus } from 'lucide-react';
import { Fragment } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../ui/Icon';
import { useCapture } from './CaptureContext';
import { useCan } from '../../lib/session';
import { NAV_PATHS, screenForPath, visibleNav } from './nav';
import { UserMenu } from './UserMenu';
import type { NavItem } from './nav';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_MINI_WIDTH = 56;

interface SidebarProps {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  readonly onToggleActivity: () => void;
}

/** Left sidebar (design Main.dc.html:25-58). */
export function Sidebar({ collapsed, onToggle, onToggleActivity }: SidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = screenForPath(pathname);
  const open = !collapsed;
  const toggleLabel = open ? 'Свернуть меню' : 'Развернуть меню';
  const nav = visibleNav(useCan());

  return (
    <aside
      aria-label="Навигация"
      className="flex flex-none flex-col border-r border-border-1 bg-bg-3 transition-[width] duration-200 ease-[cubic-bezier(0.2,0,0,1)]"
      style={{ width: open ? SIDEBAR_WIDTH : SIDEBAR_MINI_WIDTH }}
    >
      <div className={cn('flex h-14 flex-none items-center gap-2.5 border-b border-border-1 px-3', !open && 'justify-center')}>
        {/* The mini sidebar has room for one 28px control: the toggle, so the menu can open again. */}
        {open && (
          <>
            <span className="grid size-7 flex-none place-items-center rounded-[7px] bg-pi-navy text-[16px] font-bold text-pi-coral">
              π
            </span>
            <span className="flex-1 whitespace-nowrap text-[14px] font-semibold">Учёт аутсорса</span>
          </>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={toggleLabel}
          title={toggleLabel}
          className="grid size-7 flex-none place-items-center rounded-ctl border-0 bg-transparent text-fg-2 hover:bg-neutral-100"
        >
          <Icon icon={PanelLeft} />
        </button>
      </div>
      <CaptureButton open={open} />
      <nav className="flex flex-1 flex-col gap-px overflow-y-auto px-2 pb-2 pt-1">
        {nav.map((item, index) => (
          <Fragment key={item.key}>
            {open && (item.startsGroup || nav[index - 1]?.group !== item.group) && <GroupHeading>{item.group}</GroupHeading>}
            <NavEntry item={item} open={open} active={item.key === active} />
          </Fragment>
        ))}
      </nav>
      <UserMenu open={open} onToggleActivity={onToggleActivity} />
    </aside>
  );
}

function CaptureButton({ open }: { readonly open: boolean }) {
  const capture = useCapture();
  if (!capture.isAvailable) return null;
  return (
    <div className="px-2 pb-1 pt-3">
      <button
        type="button"
        onClick={() => capture.open()}
        title="Зафиксировать (N)"
        aria-label="Зафиксировать, клавиша N"
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-2 border-0 bg-pi-coral px-2.5 text-[13px] font-semibold text-on-color',
          'hover:bg-pi-coral-hover active:translate-y-px active:bg-pi-coral-press',
          open ? 'justify-start' : 'justify-center',
        )}
      >
        <Icon icon={Plus} />
        {open && (
          <>
            <span className="flex-1 text-left">Зафиксировать</span>
            <span className="rounded-kbd bg-on-coral-badge px-1.5 py-px text-[11px] font-semibold">N</span>
          </>
        )}
      </button>
    </div>
  );
}

function GroupHeading({ children }: { readonly children: string }) {
  return (
    <div className="flex h-7 items-end overflow-hidden whitespace-nowrap px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-caps text-fg-3">
      {children}
    </div>
  );
}

function NavEntry({ item, open, active }: { readonly item: NavItem; readonly open: boolean; readonly active: boolean }) {
  return (
    <Link
      to={NAV_PATHS[item.key]}
      title={item.label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex h-8 items-center gap-2.5 whitespace-nowrap rounded-ctl px-2.5 text-left text-[13px] no-underline hover:bg-neutral-100',
        active ? 'bg-pi-navy-tint font-semibold text-fg-1' : 'bg-transparent font-medium text-fg-2',
      )}
    >
      <Icon icon={item.icon} />
      {open && <span className="flex-1 overflow-hidden text-ellipsis">{item.label}</span>}
    </Link>
  );
}
